import type { IAndroidPowerStatus } from '@/helpers/parseAndroidPowerStatus'
import { parseWebBatteryStatus } from '@/helpers/parseWebBatteryStatus'

type TBatteryEventTarget = EventTarget & { charging?: unknown, level?: unknown }
type TNavigatorWithBattery = Navigator & { getBattery?: () => Promise<TBatteryEventTarget> }

async function getBatteryManager(): Promise<TBatteryEventTarget | null> {
  if (typeof navigator === 'undefined') {
    return null
  }
  const getBattery = (navigator as TNavigatorWithBattery).getBattery
  if (typeof getBattery !== 'function') {
    return null
  }
  try {
    return await getBattery.call(navigator)
  }
  catch {
    // Blocked by permissions policy / insecure context.
    return null
  }
}

/** Web (Chromium): battery level and charging state via the Battery Status API; `null` when unsupported. */
export async function fetchWebPowerStatus(): Promise<IAndroidPowerStatus | null> {
  return parseWebBatteryStatus(await getBatteryManager())
}

/** Calls `handler` when charging starts / stops. Resolves an unsubscribe fn (no-op when unsupported). */
export async function listenWebPowerSourceChange(handler: () => void): Promise<() => void> {
  const battery = await getBatteryManager()
  if (battery == null) {
    return () => {}
  }
  battery.addEventListener('chargingchange', handler)
  return () => battery.removeEventListener('chargingchange', handler)
}
